import { useEffect, useState } from "react";
import { Settings, File, Megaphone } from "lucide-react";

const MaintenanceBreak = ({
    targetDate = new Date(),
    title = "System Under Maintenance",
    message = "We're performing scheduled maintenance to improve your experience. We'll be back online soon."
}) => {
    const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());
    const [prevTime, setPrevTime] = useState(calculateTimeLeft());

    function calculateTimeLeft() {
        const difference = targetDate.getTime() - new Date().getTime();

        if (difference > 0) {
            return {
                days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                minutes: Math.floor((difference / 1000 / 60) % 60),
                seconds: Math.floor((difference / 1000) % 60)
            };
        }

        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    useEffect(() => {
        const timer = setInterval(() => {
            setPrevTime(timeLeft);
            setTimeLeft(calculateTimeLeft());
        }, 1000);

        return () => clearInterval(timer);
    }, [timeLeft]);

    const hasChanged = (current, previous) => current !== previous;

    return (
        <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden">
            {/* Animated background gradient */}
            <div className="absolute inset-0 bg-gradient-radial opacity-50" />
            <div className="absolute top-20 left-20 w-72 h-72 bg-red/10 rounded-full blur-3xl animate-pulse-glow" />
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: "1s" }} />

            <main className="relative z-10 max-w-4xl mx-auto px-6 py-12">
                <div className="text-center space-y-8 animate-fade-in">
                    {/* Icon with glow effect */}
                    <div className="inline-flex relative">
                        <div className="absolute inset-0 bg-primary blur-2xl opacity-50 animate-pulse-glow" />
                        <div className="relative bg-card border-2 border-primary/30 rounded-2xl p-6 shadow-glow">
                            <Settings className="w-16 h-16 text-primary animate-spin" style={{ animationDuration: "3s" }} />
                        </div>
                    </div>

                    {/* Title and message */}
                    <div className="space-y-4">
                        <h1 className="text-5xl md:text-6xl font-bold bg-gradient-accent bg-clip-text">
                            {title}
                        </h1>
                        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                            {message}
                        </p>
                    </div>

                    {/* Countdown timer */}
                    <div className="flex justify-center gap-4 md:gap-8 py-8">
                        {[
                            { label: "Day", value: timeLeft.days, prev: prevTime.days },
                            { label: "Hours", value: timeLeft.hours, prev: prevTime.hours },
                            { label: "Minutes", value: timeLeft.minutes, prev: prevTime.minutes },
                            { label: "Seconds", value: timeLeft.seconds, prev: prevTime.seconds }
                        ].map((item) => (
                            <div key={item.label} className="flex flex-col items-center space-y-2">
                                <div className="bg-card border border-border rounded-xl p-4 md:p-6 min-w-[80px] md:min-w-[100px] shadow-lg relative overflow-hidden">
                                    <div className="absolute inset-0 bg-gradient-accent opacity-5" />
                                    <div className="relative">
                                        <div
                                            className={`text-4xl md:text-5xl font-bold bg-gradient-accent bg-clip-text text-black transition-all ${hasChanged(item.value, item.prev) ? "animate-flip-in" : ""
                                                }`}
                                            key={item.value}
                                        >
                                            {String(item.value).padStart(2, "0")}
                                        </div>
                                    </div>
                                </div>
                                <span className="text-sm md:text-base text-muted-foreground font-medium uppercase tracking-wider">
                                    {item.label}
                                </span>
                            </div>
                        ))}
                    </div>

                    {/* Status indicators */}
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-6 pt-4">
                        <div className="flex items-center gap-2 text-muted-foreground">
                            <File className="w-5 h-5 text-primary" />
                            <a href="https://docs.everest.7span.work" className="text-sm hover:text-primary/80 transition-colors hover:underline">Read Docs</a>
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                            <Megaphone className="w-5 h-5 text-primary" />
                            <a href="https://feedback.everest.7span.work" className="text-sm hover:text-primary/80 transition-colors hover:underline">Announcements</a>
                        </div>
                    </div>

                    {/* Additional info */}
                    {/* <div className="pt-8 border-t border-border/50">
                        <p className="text-sm text-muted-foreground">
                            Need urgent assistance?{" "}
                            <a href="mailto:support@example.com" className="text-primary hover:text-primary/80 transition-colors underline">
                                Contact our support team
                            </a>
                        </p>
                    </div> */}
                </div>
            </main>
        </div>
    );
};

export default MaintenanceBreak;
